import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useRef, useState, Children, useEffect } from 'react';
import { Transition } from 'react-transition-group';
import Icon from '../Icon/Icon.js';
import { useClickOutsideElement } from '../../services/hooks/useClickOutsideElement.js';
import styles from './SelectionField.module.scss.js';

function SelectionField(_ref) {
  var name = _ref.name,
    children = _ref.children,
    onChange = _ref.onChange,
    onBeforeChange = _ref.onBeforeChange,
    defaultValue = _ref.defaultValue,
    disabled = _ref.disabled,
    dataTestId = _ref.dataTestId,
    scrollIntoView = _ref.scrollIntoView,
    className = _ref.className;
  var selectionWrapper = useRef(null);
  var selectedItemContent = useRef(null);
  var selectionButton = useRef(null);
  var selectionList = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    selectionListVisible = _useState2[0],
    setSelectionListVisible = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedValue = _useState4[0],
    setSelectedValue = _useState4[1];

  /* To be used by key events */
  var listItemIds = [];
  var ENTER_KEY_CODE = 13;
  var DOWN_ARROW_KEY_CODE = 40;
  var UP_ARROW_KEY_CODE = 38;
  var ESCAPE_KEY_CODE = 27;
  var toggleListVisibility = function toggleListVisibility() {
    setSelectionListVisible(!selectionListVisible);
  };
  var closeList = function closeList() {
    setSelectionListVisible(false);
  };
  var selectListItem = function selectListItem(value) {
    if (onBeforeChange(value)) {
      setSelectedValue(value);
      closeList();
      onChange(value);
    }
  };
  var focusNextListItem = function focusNextListItem(direction) {
    var activeElementId = document.activeElement.id;
    var currentActiveElementIndex = listItemIds.indexOf(activeElementId);
    if (direction === DOWN_ARROW_KEY_CODE) {
      var currentActiveElementIsNotLastItem = currentActiveElementIndex < listItemIds.length - 1;
      if (currentActiveElementIsNotLastItem) {
        var nextListItemId = listItemIds[currentActiveElementIndex + 1];
        document.querySelector("#".concat(nextListItemId)).focus();
      }
    } else if (direction === UP_ARROW_KEY_CODE) {
      var currentActiveElementIsNotFirstItem = currentActiveElementIndex > 0;
      if (currentActiveElementIsNotFirstItem) {
        var _nextListItemId = listItemIds[currentActiveElementIndex - 1];
        document.querySelector("#".concat(_nextListItemId)).focus();
      }
    }
  };
  var handleKeyDown = function handleKeyDown(e) {
    switch (e.keyCode) {
      case ENTER_KEY_CODE:
        selectListItem(e.currentTarget.getAttribute('value'));
        break;
      case DOWN_ARROW_KEY_CODE:
        focusNextListItem(DOWN_ARROW_KEY_CODE);
        break;
      case UP_ARROW_KEY_CODE:
        focusNextListItem(UP_ARROW_KEY_CODE);
        break;
      case ESCAPE_KEY_CODE:
        closeList();
        break;
    }
  };
  var items = children ? Children.map(children, function (child, index) {
    listItemIds.push("selection-list-item-".concat(name, "-").concat(index));
    var val = null;
    if (child.props) {
      val = child.props.value;
    }
    return /*#__PURE__*/React.createElement("li", {
      key: index,
      className: classNames(styles.listItem, child.props.disabled && styles.disabled, child.props.hidden && styles.hidden),
      onClick: function onClick() {
        return selectListItem(val);
      },
      onKeyDown: handleKeyDown,
      id: "selection-list-item-".concat(name, "-").concat(index),
      tabIndex: "0",
      value: val
    }, child);
  }) : [];
  var chooseSelection = function chooseSelection() {
    if (!defaultValue) {
      if (items.length > 0) {
        selectListItem(items[0].props.value);
      }
    } else {
      selectListItem(defaultValue);
    }
  };
  useClickOutsideElement(selectionWrapper, closeList, selectionListVisible);
  useEffect(function () {
    chooseSelection();
  }, [defaultValue]);
  var defaultItem = defaultValue && items.length > 0 ? items[items.findIndex(function (i) {
    return i.props.value === defaultValue;
  })] : items[0];
  return /*#__PURE__*/React.createElement("div", {
    className: styles.selectionWrapper,
    ref: selectionWrapper
  }, /*#__PURE__*/React.createElement("button", {
    name: name,
    value: selectedValue,
    onClick: toggleListVisibility,
    className: styles.selectionButton,
    ref: selectionButton,
    disabled: disabled,
    id: dataTestId,
    "data-testid": "selectionFieldButton"
  }, /*#__PURE__*/React.createElement("div", {
    ref: selectedItemContent,
    className: styles.selectedValue
  }, /*#__PURE__*/React.createElement("ul", null, defaultItem)), /*#__PURE__*/React.createElement(Icon, {
    icon: "icon-dropdown-arrow-down",
    size: "medium"
  })), /*#__PURE__*/React.createElement(Transition, {
    "in": selectionListVisible,
    timeout: 0
  }, function (state) {
    var combinedclassName = classNames(styles.selectionListWrapper, styles["selection-list-wrapper-".concat(state)], className);
    if (selectionListVisible && scrollIntoView && state === 'entered') {
      selectionList.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    }
    return /*#__PURE__*/React.createElement("ul", {
      className: combinedclassName,
      ref: selectionList,
      id: "".concat(dataTestId, "-wrapper"),
      "data-testid": "selectionFieldWrapper"
    }, items);
  }));
}
SelectionField.propTypes = {
  children: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  onBeforeChange: PropTypes.func,
  onChange: PropTypes.func,
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  scrollIntoView: PropTypes.bool,
  dataTestId: PropTypes.string,
  className: PropTypes.string
};
SelectionField.defaultProps = {
  onChange: function onChange() {},
  onBeforeChange: function onBeforeChange() {
    return true;
  },
  defaultValue: null,
  disabled: false,
  scrollIntoView: false,
  dataTestId: null,
  className: ''
};

export { SelectionField as default };
